import React, { useEffect, useState } from 'react';
import { Card, CardBody, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { PageService } from './service';
import { ErrorToast, SuccessToast } from '../../shared/toasters/toasters';
import DeleteModal from '../../Common/DeleteModal';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import Pagination from '../../Common/Pagination';

const PageList = () => {
    const { t } = useTranslation(); // Initialize translation hook
    const [list, setList] = useState([]);
    const [totalPages, setTotalPages] = useState(0);
    const [term, setTerm] = useState('');
    const [entriesPerPage, setEntriesPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [pageToDelete, setPageToDelete] = useState<number | null>(null);
    const navigate = useNavigate();

    const entriesPerPageOptions = [
        { label: '5', value: 5 },
        { label: '10', value: 10 },
        { label: '20', value: 20 },
        { label: '30', value: 30 },
        { label: '40', value: 40 },
    ];

    const handleTermChange = (e: any) => {
        setTerm(e.target.value);
        setCurrentPage(1);
    };

    const handleEntriesPerPageChange = (selectedOption: any) => {
        setEntriesPerPage(selectedOption ? selectedOption.value : 10);
        setCurrentPage(1);
    };

    const handlePageChange = (pageNumber: number) => {
        setCurrentPage(pageNumber);
    };

    const handleDeleteClick = (id: number) => {
        setPageToDelete(id);
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = async () => {
        if (pageToDelete !== null) {
            try {
                await PageService.delete(pageToDelete);
                SuccessToast(t('page.successDelete'));
                setShowDeleteModal(false);
                setPageToDelete(null);
                fetchData();
            } catch (error) {
                ErrorToast(error);
            }
        }
    };

    const fetchData = async () => {
        try {
            const response = await PageService.getAll({
                page: currentPage,
                perPage: entriesPerPage,
                term,
            });
            const data = response.data;
            setList(data);
            setTotalPages(response.totalPages);
        } catch (error) {
            ErrorToast(error);
        }
    };

    useEffect(() => {
        fetchData();
    }, [currentPage, entriesPerPage, term]);

    return (
        <React.Fragment>
            <Col>
                <Card>
                    <CardBody className="card-body">
                        <div className="text-end">
                            <button
                                className="btn btn-primary text-end"
                                onClick={() => navigate('/pages/create')}
                            >
                                <i className="ti ti-plus f-18"></i> {t('page.add')}
                            </button>
                        </div>

                        <div className="d-sm-flex align-items-center mb-3">
                            <ul className="list-inline me-auto my-1">
                                <li className="list-inline-item">
                                    <Select
                                        placeholder={t('page.entriesPerPage')}
                                        options={entriesPerPageOptions}
                                        onChange={handleEntriesPerPageChange}
                                        defaultValue={entriesPerPageOptions.find(
                                            (option) => option.value === entriesPerPage
                                        )}
                                        classNamePrefix="react-select"
                                    />
                                </li>
                                <span> {t('page.entriesPerPage')}</span>
                            </ul>
                            <ul className="list-inline ms-auto my-1 mt-3">
                                <li className="list-inline-item">
                                    <form className="">
                                        <input
                                            type="search"
                                            className="form-control"
                                            placeholder={t('page.searchPlaceholder')}
                                            onChange={handleTermChange}
                                        />
                                    </form>
                                </li>
                            </ul>
                        </div>
                        <div className="table-responsive">
                            <table className="table table-hover sticky-actions" id="pc-dt-simple-1">
                                <thead>
                                    <tr>
                                        <th>{t('page.id')}</th>
                                        <th>{t('page.title')}</th>
                                        <th className="text-end sticky-col">{t('page.actions')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {list.map((item: any, index: number) => (
                                        <tr key={index}>
                                            <td>{item.id}</td>
                                            <td className="wrap-col">{item.title}</td>
                                            <td className="text-end text-nowrap sticky-col">
                                                <ul className="list-inline mb-0">
                                                    <li
                                                        className="list-inline-item"
                                                        onClick={() => navigate(`/pages/edit/${item.id}`)}
                                                    >
                                                        <a
                                                            href="#"
                                                            className="avtar avtar-s btn-link-success btn-pc-default"
                                                        >
                                                            <i className="ti ti-edit f-20"></i>
                                                        </a>
                                                    </li>
                                                    <li className="list-inline-item">
                                                        <span
                                                            role="button"
                                                            className="avtar avtar-s btn-link-danger btn-pc-default"
                                                            onClick={() => handleDeleteClick(item.id)}
                                                        >
                                                            <i className="ti ti-trash f-20"></i>
                                                        </span>
                                                    </li>
                                                </ul>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <Pagination
                          currentPage={currentPage}
                          totalPages={totalPages}
                          onPageChange={handlePageChange}
                          previousLabel={t('page.previous')}
                          nextLabel={t('page.next')}
                        />
                    </CardBody>
                </Card>
            </Col>

            <DeleteModal
                show={showDeleteModal}
                handleClose={() => setShowDeleteModal(false)}
                handleDeleteId={handleDeleteConfirm}
                name={t('page.name')}
            />
        </React.Fragment>
    );
};

export default PageList;